import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, PageHeader, PagePaper } from 'dashboard/components/common';
import DiscountDialog from './DiscountDialog';
import DiscountStats  from './DiscountStats';
import DiscountsTable from './DiscountsTable';

/**
 * Promotions list — coupon codes the brand owns.
 *
 * This file owns: data loading (both the discount list and the
 * best-effort usage report), the create/edit dialog state, and the
 * submit/delete handlers. The table UI lives in `DiscountsTable.jsx`;
 * the usage summary tiles already lived in `DiscountStats.jsx`.
 */
export default function Discounts() {
  const [selectedBrand] = useOutletContext();
  const [list, setList] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return Promise.all([
      API.listBrandDiscounts({ brand_id: selectedBrand }),
      // Usage report is best-effort — promotions page still works
      // without it if the orders/discount join hits a snag.
      API.brandDiscountUsage({ brand_id: selectedBrand }).catch(() => []),
    ])
      .then(([discounts, usageRows]) => {
        setList(Array.isArray(discounts) ? discounts : []);
        setUsage(Array.isArray(usageRows) ? usageRows : []);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load discounts', { toastId: 'discounts-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      API.listBrandDiscounts({ brand_id: selectedBrand }),
      API.brandDiscountUsage({ brand_id: selectedBrand }).catch(() => []),
    ])
      .then(([discounts, usageRows]) => {
        if (cancelled) return;
        setList(Array.isArray(discounts) ? discounts : []);
        setUsage(Array.isArray(usageRows) ? usageRows : []);
      })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load discounts', { toastId: 'discounts-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  // Keyed by `_id` so per-row lookups in the table are O(1).
  const usageById = useMemo(() => {
    const map = {};
    (usage || []).forEach((u) => { map[String(u._id)] = u; });
    return map;
  }, [usage]);

  const usageTotals = useMemo(() => {
    let uses = 0; let revenue = 0; let discounted = 0;
    (usage || []).forEach((u) => {
      uses += Number(u.uses || 0);
      revenue += Number(u.revenue || 0);
      discounted += Number(u.discount_total || 0);
    });
    return { uses, revenue, discounted };
  }, [usage]);

  const openNew  = ()  => { setEditing(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d);    setOpen(true); };

  const handleSubmit = (form) => {
    setSubmitting(true);
    const fn = editing
      ? API.updateBrandDiscount({ brand_id: selectedBrand, _id: editing._id, patch: form })
      : API.createBrandDiscount({ brand_id: selectedBrand, ...form });
    fn.then(() => {
      toast.success(editing ? 'Discount updated' : 'Discount created');
      setOpen(false);
      load();
    })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSubmitting(false));
  };

  const handleDelete = (d) => {
    if (!window.confirm(`Delete ${d.code}?`)) return;
    API.deleteBrandDiscount({ brand_id: selectedBrand, _id: d._id })
      .then(() => { toast.success('Deleted'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Promotions'
          subtitle='Coupon codes customers type in at checkout. The brand storefront and Stripe Checkout both honor active codes automatically.'
          actions={
            <Button variant='contained' startIcon={<AddIcon />} onClick={openNew}>
              New discount
            </Button>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to manage promotions.</EmptyState>}

        {selectedBrand && usageTotals.uses > 0 && <DiscountStats totals={usageTotals} />}

        {selectedBrand && (
          <DiscountsTable
            rows={list}
            loading={loading}
            usageById={usageById}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        )}
      </PagePaper>

      <DiscountDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        initial={editing}
      />
    </Box>
  );
}
